import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSyncConfigV1Service, serviceName } from '../service';
import { GetAllArtifactsGroupedIO, ServiceQueries } from '../types';
import {
  UseSyncConfigV1InfiniteQueryEndpointArgs,
  useSyncConfigV1InfiniteQuery,
} from './use-sync-config-v1-infinite-query';

type GetAllArtifactsGroupedPageParam = string;
type GetAllArtifactsGroupedWireResponse = GetAllArtifactsGroupedIO['output'] & {
  next_page_token?: string;
};
/**
 * A hook that returns an infinite query for the `GetAllArtifactsGrouped` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetAllArtifactsGroupedQuery = <E = Error, D = GetAllArtifactsGroupedIO['output']>({
  options,
  request,
  ...args
}: UseSyncConfigV1InfiniteQueryEndpointArgs<'GetAllArtifactsGrouped', E, D, GetAllArtifactsGroupedPageParam>) =>
  useSyncConfigV1InfiniteQuery<'GetAllArtifactsGrouped', E, D, GetAllArtifactsGroupedPageParam>({
    endpointName: 'GetAllArtifactsGrouped',
    ...args,
    request: ({ pageParam }) => {
      const requestWithPageParams: GetAllArtifactsGroupedIO['input'] = {
        ...request,
        pageToken: pageParam ?? request.pageToken,
      };
      return requestWithPageParams;
    },
    options: {
      ...options,
      getNextPageParam: (lastPage) => {
        const response = lastPage as GetAllArtifactsGroupedWireResponse;
        const nextPageToken = response.nextPageToken ?? response.next_page_token;

        return nextPageToken || undefined;
      },
    },
  });

/**
 * A hook that returns a function to fetch the query for the `GetAllArtifactsGrouped` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetAllArtifactsGrouped = () => {
  const queryClient = useQueryClient();

  const fetchGetAllArtifactsGrouped = async <E = Error, D = GetAllArtifactsGroupedIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSyncConfigV1InfiniteQueryEndpointArgs<'GetAllArtifactsGrouped', E, D>, 'options'> & {
    options: LimitedSchemaFetchQueryOptions<
      GetAllArtifactsGroupedIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetAllArtifactsGrouped'>>
    >;
  }) => {
    try {
      const response = await queryClient.fetchQuery<
        GetAllArtifactsGroupedIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetAllArtifactsGrouped'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetAllArtifactsGrouped'>({
          serviceName,
          endpointName: 'GetAllArtifactsGrouped',
          request,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSyncConfigV1Service.GetAllArtifactsGrouped(request, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError ?? ({} as D);
      }
      throw error;
    }
  };

  return fetchGetAllArtifactsGrouped;
};
